/**
 * Custom Infinite Scroll for Collection Page
 * 
 * Logic:
 * 1. Watch the .js-infinite-scroll element using IntersectionObserver.
 * 2. When it comes into view, fetch the next page URL (data-next-url).
 * 3. Parse the HTML response.
 * 4. Append new products to .js-grid-wrapper.
 * 5. Update the .js-infinite-scroll element's data-next-url with the new page's next URL.
 * 6. If no more pages, hide/remove the infinite scroll element.
 */
document.addEventListener("DOMContentLoaded", function () {
    const infiniteScrollEl = document.querySelector(".js-infinite-scroll");
    const gridWrapper = document.querySelector(".js-grid-wrapper");

    if (!infiniteScrollEl || !gridWrapper) return;

    let isLoading = false;
    let isDone = false;
    let checkFrame = null;
    let passedTriggerCooldownUntil = 0;
    let failureCount = 0;
    let retryNotBefore = 0;
    let retryPaused = false;
    const maxAutomaticRetries = 3;
    const isCroatian = (window.ShopifyCurrentLanguage || document.documentElement.lang || '').startsWith('hr');
    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = 'button button--secondary';
    retryButton.hidden = true;
    retryButton.textContent = isCroatian ? 'Učitavanje nije uspjelo. Pokušajte ponovno.' : 'Loading failed. Try again.';
    infiniteScrollEl.appendChild(retryButton);
    retryButton.addEventListener('click', () => {
        if (isLoading) return;
        failureCount = 0;
        retryNotBefore = 0;
        retryPaused = false;
        retryButton.hidden = true;
        requestPassedLoadCheck();
    });
    const selectedSize = Array.from(new URLSearchParams(window.location.search).entries()).find(([key]) => ["option.size", "option.beden", "option.boyut", "option.velicina", "option.veličina"].some((name) => key.toLowerCase().includes(name)))?.[1] || null; const filterUnavailableSelectedSizeProducts = (root) => { if (!selectedSize) return; root.querySelectorAll(".collection__item").forEach((item) => { const swatch = Array.from(item.querySelectorAll(".product-card__size")).find((size) => size.textContent.trim() === selectedSize); if (!swatch || swatch.classList.contains("product-card__size--unavailable")) item.remove(); }); }; filterUnavailableSelectedSizeProducts(gridWrapper);
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                requestLoadCheck();
            }
        });
    }, {
        rootMargin: "200px 0px 400px 0px",
        threshold: 0
    });

    observer.observe(infiniteScrollEl);

    window.addEventListener("scroll", requestPassedLoadCheck, { passive: true });
    window.addEventListener("resize", requestPassedLoadCheck);
    window.addEventListener("orientationchange", requestPassedLoadCheck);

    requestPassedLoadCheck();
    setTimeout(requestPassedLoadCheck, 250);
    setTimeout(requestPassedLoadCheck, 1000);

    function requestPassedLoadCheck() {
        requestLoadCheck(true);
    }

    function requestLoadCheck(allowPassedTrigger = false) {
        if (checkFrame) return;

        checkFrame = requestAnimationFrame(() => {
            checkFrame = null;

            if (isDone || isLoading || retryPaused || Date.now() < retryNotBefore || !document.body.contains(infiniteScrollEl)) return;

            if (isLoadTriggerVisible() || (allowPassedTrigger && isLoadTriggerReached())) {
                loadNextPage();
            }
        });
    }

    function isLoadTriggerVisible() {
        if (infiniteScrollEl.classList.contains("is-hidden")) return false;

        const rect = infiniteScrollEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        return rect.bottom >= 0 && rect.top <= viewportHeight;
    }

    function isLoadTriggerReached() {
        if (infiniteScrollEl.classList.contains("is-hidden")) return false;
        if (Date.now() < passedTriggerCooldownUntil) return false;

        const rect = infiniteScrollEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        return rect.top <= viewportHeight;
    }

    async function loadNextPage() {
        if (isLoading || isDone) return;

        const nextUrl = infiniteScrollEl.getAttribute("data-next-url");
        if (!nextUrl) {
            finishInfiniteScroll();
            return;
        }

        isLoading = true;
        infiniteScrollEl.classList.add("loading");

        try {
            const requestUrl = new URL(nextUrl, window.location.origin);
            const response = await fetch(requestUrl.toString());

            if (!response.ok) {
                const error = new Error(`Infinite scroll request failed: ${response.status}`);
                error.permanent = response.status >= 400 && response.status < 500 && response.status !== 429;
                throw error;
            }

            const text = await response.text();
            const doc = new DOMParser().parseFromString(text, "text/html");
            const newGridWrapper = doc.querySelector(".js-grid-wrapper");
            if (!newGridWrapper) throw new Error('Collection grid missing from response');
            const newProducts = newGridWrapper ? newGridWrapper.innerHTML : "";
            const newInfiniteScroll = doc.querySelector(".js-infinite-scroll");
            const newNextUrl = newInfiniteScroll ? newInfiniteScroll.getAttribute("data-next-url") : "";
            if (newNextUrl && newNextUrl === nextUrl) throw new Error('Pagination did not advance');

            if (newProducts) {
                gridWrapper.insertAdjacentHTML("beforeend", newProducts);
                filterUnavailableSelectedSizeProducts(gridWrapper); passedTriggerCooldownUntil = Date.now() + 600;
            }

            failureCount = 0;
            retryNotBefore = 0;
            retryButton.hidden = true;

            if (newNextUrl) {
                infiniteScrollEl.setAttribute("data-next-url", newNextUrl);
            } else {
                finishInfiniteScroll();
            }
        } catch (err) {
            console.error("Infinite scroll failed:", err);
            failureCount++;
            retryPaused = Boolean(err.permanent) || failureCount > maxAutomaticRetries;
            retryNotBefore = Date.now() + Math.min(8000, 1000 * 2 ** (failureCount - 1));
            retryButton.hidden = !retryPaused;
        } finally {
            isLoading = false;
            infiniteScrollEl.classList.remove("loading");
            if (!isDone && !retryPaused) {
                const retryDelay = Math.max(0, Math.max(passedTriggerCooldownUntil, retryNotBefore) - Date.now()) + 50;
                setTimeout(requestPassedLoadCheck, retryDelay);
            }
        }
    }

    function finishInfiniteScroll() {
        isDone = true;
        observer.disconnect();
        window.removeEventListener("scroll", requestPassedLoadCheck);
        window.removeEventListener("resize", requestPassedLoadCheck);
        window.removeEventListener("orientationchange", requestPassedLoadCheck);
        infiniteScrollEl.remove();
    }
});
